/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { LoggerService } from '../../../../shared/services/logger.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Select, Store } from '@ngxs/store';
import { SelectUploader } from './state/upload.actions';
import { FileUploadDialogData, UploaderType } from './state/upload.model';
import { UploaderState } from './state/upload.state';
import { from, Observable } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { StorageService } from '../../../../shared/services/storage.service';
import { DataFormService } from '../../../services/dataform.service';
import { DeviceService } from '../../../../shared/services/device.service';
import { DataFormContentComponent } from '../../../dataForm/data-form-content.component';

@Component({
    selector: 'dash-gis-upload-dialog',
    templateUrl: './upload-dialog.component.html',
    styleUrls: ['./upload-dialog.component.scss'],
})
export class UploadDialogComponent implements OnInit {
    @Select(UploaderState.uploader) uploader$: Observable<UploaderType>;

    displayName = 'Upload Files';
    formValid = false;
    filesToUpload: File[] = [];
    hasVideoInput = false;

    @ViewChild(DataFormContentComponent) dataFormContent: DataFormContentComponent;

    public constructor(
        private logger: LoggerService,
        private dialogRef: MatDialogRef<UploadDialogComponent>,
        private dataFormService: DataFormService,
        private deviceService: DeviceService,
        private storageService: StorageService,
        private store: Store,
        @Inject(MAT_DIALOG_DATA) public data: FileUploadDialogData
    ) {}

    ngOnInit(): void {
        // check once for camera existing
        // this.deviceService.hasVideoInput$.subscribe(hasVideo => this.hasVideoInput$ = hasVideo);
    }

    useCameraUpload() {
        this.store.dispatch(new SelectUploader({ uploader: UploaderType.CAMERA }));
    }

    useFileUpload() {
        this.store.dispatch(new SelectUploader({ uploader: UploaderType.FILE }));
    }

    setDisplayName(name: string) {
        this.displayName = name;
    }

    isFormValid(valid: boolean) {
        this.formValid = valid;
    }

    shouldCloseDialog(success: boolean) {
        this.dialogRef.close(success);
    }

    cancel() {
        this.dialogRef.close(false);
    }

    /**
     * Add new elements to list of files that will be uploaded, excluding duplicates identified
     * by name and size
     * @param files
     */
    public setFileToUpload(files: File[]) {
        files.forEach((newFile) => {
            if (
                -1 ===
                this.filesToUpload.findIndex(
                    (currentFile) => currentFile.name === newFile.name && currentFile.size === newFile.size
                )
            ) {
                this.filesToUpload.push(newFile);
            }
        });
        this.dataFormContent.dataForm.updateValueAndValidity();
    }

    public doUpload() {
        const files = this.filesToUpload;
        from(files)
            .pipe(
                concatMap((f) => this.storageService.storeFile(f)),
                map((savedFile) => {
                    const form = this.dataFormContent.dataForm;
                    form.controls['hash'].markAsTouched();
                    form.controls['hash'].markAsDirty();
                    form.controls['hash'].setValue(savedFile.key);
                    form.controls['filename'].markAsTouched();
                    form.controls['filename'].markAsDirty();
                    form.controls['filename'].setValue(savedFile.originalName);
                    form.controls['filetype'].markAsTouched();
                    form.controls['filetype'].markAsDirty();
                    form.controls['filetype'].setValue(savedFile.mimeType);
                    form.updateValueAndValidity();
                    this.dataFormContent.saveData();
                })
            )
            .subscribe({
                next: null,
                error: null,
                complete: () => this.dialogRef.close(true),
            });
    }

    removeFile(index: number) {
        this.filesToUpload.splice(index, 1);
    }

    get canUseCamera() {
        return this.deviceService.canCaptureUserMedia;
    }

    get isReadyToUpload() {
        return this.formValid && this.filesToUpload.length > 0;
    }

    get acceptMimeTypes(): string[] {
        return ['image/.+', 'application/pdf'];
    }

    get acceptFileNames(): string[] {
        return [];
    }
}
